import { useQuery } from '@tanstack/react-query';
import { ReactFlowInstance, ReactFlowProvider, XYPosition, useEdgesState, useNodesState } from '@xyflow/react';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import HistoryIcon from '@mui/icons-material/History';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayIcon from '@mui/icons-material/PlayCircleFilled';
import SendIcon from '@mui/icons-material/Send';
import SensorsIcon from '@mui/icons-material/Sensors';
import TuneIcon from '@mui/icons-material/Tune';
import { ButtonGroup, Chip, Drawer, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';

import { applyObjectDefaultParameters, useApiClient, useCopyToClipboard, useIsMobile, useKeyDown, useToastService, uuid } from '@@common';
import { ErrorBoundary, ViewTitleBar, useNotificationSubscription, useNotifications } from '@@components';

import { ActivityMetadataModel, FlowClient, FlowInstanceStatus, FlowInstanceViewModel, ParameterType } from '../../api/api';
import { serializeWithTypeFirst } from '../../common/serializeWithTypeFirst';
import { useDesignerScreenContext } from '../../components/Chat/DesignerScreenContext';
import { ExpressionMetadataProvider } from '../../components/ExpressionMetadataContext';
import ResponsivePanelContainer from '../../components/Panels/ResponsivePanelContainer';
import { PresenceContext } from '../../components/Presence/PresenceContext';
import { PresenceIndicator, viewersForItem } from '../../components/Presence/PresenceIndicator';
import { usePresence } from '../../components/Presence/usePresence';
import { ActionPanel } from './ActionPanel/ActionPanel';
import { ActivityCategories, ActivityTypes } from './Components/ActivityIcon';
import FlowMetadataProvider from './Components/Contexts/FlowMetadataContext';
import { FlowSchemaContext } from './Components/Contexts/FlowSchemaContext';
import { FlowValidationContext } from './Components/Contexts/FlowValidationContext';
import { ServiceConnectionMetadataProvider } from './Components/Contexts/index';
import FlowConnectionBindingsDialog from './Components/FlowConnectionBindingsDialog';
import FlowInstanceDetailsPanel from './Components/FlowInstanceDetailsPanel';
import FlowInstancesPanel, { FLOW_INSTANCES_PANEL_DEFAULT_WIDTH } from './Components/FlowInstancesPanel';
import FlowSaveErrorDialog from './Components/FlowSaveErrorDialog';
import FlowWorkspace from './Components/FlowWorkspace';
import { nodeFromActivity } from './Components/Nodes/ActivityNode';
import { nodeFromBlock } from './Components/Nodes/BlockNode';
import { nodeFromGroup } from './Components/Nodes/GroupNode';
import { nodeFromNote } from './Components/Nodes/NoteNode';
import { nodeFromSection } from './Components/Nodes/SectionNode';
import PasteHandler from './Components/PasteHandler';
import RunFlowModal from './Components/RunFlowModal';
import { ViewLoader } from './Components/ViewLoader';
import useFlowMetadata from './Hooks/Metadata/useFlowMetadata';
import { useFlowExecutionTrace } from './Hooks/useFlowExecutionTrace';
import useSelectedFlowInstanceUrlState from './Hooks/useSelectedFlowInstanceUrlState';
import { useTrackedFlowRun } from './Hooks/useTrackedFlowRun';
import { ActivityModel, FlowDefinitionModel, FlowEdgeModel, FlowNodeModel } from './Models/FlowModel';
import { FlowSaveError, parseFlowSaveError } from './Models/FlowSaveError';
import { FlowEdge, FlowNode } from './Models/GraphModel';
import { ServiceConnectionRefModel } from './Models/ServiceConnectionModel';
import { groupActivityValidationIssues, visibleActivityValidationIssues } from './Services/ActivityValidationIssues';
import { isContainerContentHidden, updateAgentServiceCompact } from './Services/AgentServiceCompact';
import { EntitySchemaRepository, IEntitySchemaRepository } from './Services/EntitySchemaRepository';
import { countFlowSchemaUses, resolveFlowSchemaValue } from './Services/FlowSchemaValues';
import { createCommittedGraphNodes, createStableGraphNodes } from './Services/GraphRenderState';
import { getLatestFlowActivityEvent } from './Services/getFlowInstanceLiveState';

const MemoizedActionPanel = memo(ActionPanel);
const emptyActivityValidationIssues: ReturnType<typeof groupActivityValidationIssues> = new Map();

async function saveFlow(
	setSavePending: (v: boolean) => void,
	flowId: string | undefined,
	flowName: string,
	flowDefinition: FlowDefinitionModel,
	client: FlowClient,
) {
	setSavePending(true);
	try {
		const flowDefinitionJson = serializeWithTypeFirst(flowDefinition);
		console.log('Saving flow definition', flowDefinitionJson);

		if (flowId) {
			await client.update(flowId, {
				name: flowName,
				definition: flowDefinitionJson,
			});
			return flowId;
		} else {
			const newId = await client.create({
				name: flowName,
				definition: flowDefinitionJson,
				tags: [],
			});
			return newId;
		}
	} finally {
		setSavePending(false);
	}
}

/**
 * Finds an existing service connection in the flow that matches the specified type or category.
 * This allows new activities to automatically inherit service connections from existing activities.
 */
function findMatchingServiceConnection(
	nodes: FlowNode[],
	activityByName: Map<string, ActivityMetadataModel>,
	matchType?: string,
	matchCategory?: string,
): ServiceConnectionRefModel | undefined {
	if (!matchType && !matchCategory) {
		return undefined;
	}

	// Iterate through all existing nodes to find one with a matching service connection
	for (const node of nodes) {
		const activityModel = node.data as ActivityModel;
		const activityMetadata = activityByName.get(activityModel.Type);

		if (!activityMetadata || !activityMetadata.parameters) {
			continue;
		}

		// Check each parameter in the activity
		for (const param of activityMetadata.parameters) {
			if (param.type === ParameterType.ServiceConnectionRef) {
				// Check if this parameter matches the requested type or category
				const typeMatches = matchType && param.matchType === matchType;
				const categoryMatches = matchCategory && param.matchCategory === matchCategory;

				if (typeMatches || categoryMatches) {
					// Get the actual service connection value from the activity model
					const serviceConnectionRef = activityModel[param.name] as ServiceConnectionRefModel | undefined;
					if (serviceConnectionRef && serviceConnectionRef.Id) {
						return serviceConnectionRef;
					}
				}
			}
		}
	}

	return undefined;
}

type FlowDesignerViewUrlParams = {
	id: string | undefined;
};

const SAVE_KEYS = ['s', 'S'];
const FLOW_INSTANCE_PANELS_TOP = 48;
type SelectedInstanceRuntimeState = {
	status?: FlowInstanceStatus;
	currentActivityId?: string;
};

function getFlowDefinitionSnapshot(flowDefinition: FlowDefinitionModel): string {
	return serializeWithTypeFirst({
		...flowDefinition,
		Id: '',
	});
}

function FlowDesignerContent() {
	const isMobile = useIsMobile();
	const { flowModelConverterService, activityByName, clipboardService, flowMetadata, expressionScopeCalculator } = useFlowMetadata();

	const { id: routeId } = useParams<FlowDesignerViewUrlParams>();
	const { selectedInstanceId, selectInstance, clearInstance } = useSelectedFlowInstanceUrlState();

	const navigate = useNavigate();
	const toastService = useToastService();
	const copyToClipboard = useCopyToClipboard();
	const { flowInstanceEvents } = useNotifications();
	const latestStatusEvent = flowInstanceEvents.find((event) => event.payload.id === selectedInstanceId && event.payload.status);
	const latestActivityEvent = getLatestFlowActivityEvent(flowInstanceEvents, selectedInstanceId);

	const id = useMemo(() => (routeId && routeId !== 'new' ? routeId : undefined), [routeId]);
	useNotificationSubscription('flowDefinition', id);
	const [name, setName] = useState<string>('New Flow');

	const client = useApiClient(FlowClient);

	const { data: flow, isLoading: flowLoading } = useQuery({
		queryKey: ['flow', 'getById', id],
		queryFn: async () => {
			if (!id) return undefined;
			return await client.getById(id);
		},
		enabled: !!id, // Only run this query if id is defined
	});

	const [nodes, setNodes] = useNodesState<FlowNode>([]);
	const [edges, setEdges] = useEdgesState<FlowEdge>([]);
	const presenceNode = nodes.find((node) => node.selected);
	const presenceEdge = edges.find((edge) => edge.selected);
	useDesignerScreenContext({
		ScreenArea: 'Flow',
		ScreenName: name,
		FlowDefinitionId: id,
		CurrentActivityId: presenceNode?.id,
		CurrentTransitionId: presenceEdge?.id,
		FlowInstanceId: selectedInstanceId ?? undefined,
		ScreenSelectionType: presenceNode ? 'Activity' : presenceEdge ? 'Transition' : undefined,
	});
	const presenceLocation = presenceNode
		? { itemType: 'activity', itemId: presenceNode.id }
		: presenceEdge
			? { itemType: 'transition', itemId: presenceEdge.id }
			: null;
	const flowViewers = usePresence('flow', flow ? id : undefined, 'definition', presenceLocation);
	const selectionViewers = presenceLocation ? viewersForItem(flowViewers, presenceLocation.itemType, presenceLocation.itemId) : [];
	const liveGraph = useRef({ nodes, edges });
	liveGraph.current = { nodes, edges };
	const selectCommittedNodes = useMemo(() => createCommittedGraphNodes<FlowNode>(), [id]);
	const selectStructuralNodes = useMemo(() => createStableGraphNodes<FlowNode>(), [id]);
	const committedNodes = selectCommittedNodes(nodes);
	const structuralNodes = selectStructuralNodes(nodes);
	const isDragging = nodes.some((node) => node.dragging);
	const [instance, setInstance] = useState<ReactFlowInstance<FlowNode, FlowEdge> | undefined>();

	const [flowModel, setFlowModel] = useState<FlowDefinitionModel>({} as FlowDefinitionModel);
	const flowExpressionScopeCalculator = useMemo(
		() => expressionScopeCalculator?.withSchemaValues(flowModel.SchemaValues ?? []) ?? null,
		[expressionScopeCalculator, flowModel.SchemaValues],
	);

	const workspaceContainerRef = useRef<HTMLDivElement>(null);

	const [savePending, setSavePending] = useState(false);
	const [saveError, setSaveError] = useState<FlowSaveError>();
	const [activityValidation, setActivityValidation] = useState<{
		flowId: string | undefined;
		error: FlowSaveError;
		issues: ReturnType<typeof groupActivityValidationIssues>;
	}>();
	const visibleValidationIssues = useMemo(
		() =>
			activityValidation && activityValidation.flowId === id
				? visibleActivityValidationIssues(activityValidation.issues, nodes)
				: emptyActivityValidationIssues,
		[activityValidation, id, nodes],
	);
	const validationIssueCount = activityValidation?.flowId === id ? (activityValidation?.error.validationIssues.length ?? 0) : 0;
	const [runFlowModalOpen, setRunFlowModalOpen] = useState(false);
	const [bindingsDialogOpen, setBindingsDialogOpen] = useState(false);
	const [trackLiveRuns, setTrackLiveRuns] = useState(false);
	const [instancesPanelOpen, setInstancesPanelOpen] = useState(!!selectedInstanceId);
	const [instancesPanelCollapsed, setInstancesPanelCollapsed] = useState(false);
	const [instancesPanelWidth, setInstancesPanelWidth] = useState(FLOW_INSTANCES_PANEL_DEFAULT_WIDTH);
	const [actionPanelOpen, setActionPanelOpen] = useState(false);
	const [actionPanelCollapsed, setActionPanelCollapsed] = useState(false);
	const [instancesRefreshTrigger, setInstancesRefreshTrigger] = useState(0);
	const [instanceRuntime, setInstanceRuntime] = useState<{ instanceId: string | null; state: SelectedInstanceRuntimeState }>({ instanceId: null, state: {} });
	const selectedInstanceRuntimeState = instanceRuntime.instanceId === selectedInstanceId ? instanceRuntime.state : {};
	const setSelectedInstanceRuntimeState = useCallback(
		(update: (current: SelectedInstanceRuntimeState) => SelectedInstanceRuntimeState) => {
			setInstanceRuntime((current) => {
				const state = current.instanceId === selectedInstanceId ? current.state : {};
				const next = update(state);
				return current.instanceId === selectedInstanceId && next === state ? current : { instanceId: selectedInstanceId, state: next };
			});
		},
		[selectedInstanceId],
	);
	const [actionsMenuAnchor, setActionsMenuAnchor] = useState<HTMLElement | null>(null);
	const [savedFlowSnapshot, setSavedFlowSnapshot] = useState<string | undefined>();
	const isSystemFlow = flow?.isReadOnly === true;
	const isSystemOwned = flow?.isSystemOwned === true;
	const { data: connectionBindings, isError: connectionBindingsError } = useQuery({
		queryKey: ['flow', 'connectionBindings', id],
		queryFn: () => client.getConnectionBindings(id!),
		enabled: isSystemOwned && !!id,
	});

	useEffect(() => {
		if (!isMobile) {
			setActionPanelOpen(false);
		}
	}, [isMobile]);

	const currentFlowDefinition = useMemo(
		() => flowModelConverterService.toFlowDefinitionModel(edges, committedNodes, id, name, flowModel),
		[edges, flowModel, flowModelConverterService, id, name, committedNodes],
	);
	const currentFlowSnapshot = useMemo(() => getFlowDefinitionSnapshot(currentFlowDefinition), [currentFlowDefinition]);
	const hasFlowChanges = savedFlowSnapshot !== undefined && (isDragging || currentFlowSnapshot !== savedFlowSnapshot);
	const trackedRun = useTrackedFlowRun(id, trackLiveRuns && !selectedInstanceId, flowInstanceEvents);
	const displayedInstanceId = selectedInstanceId ?? trackedRun.instanceId ?? null;
	const { executionTrace, setHistoricalExecutionSteps, clearExecutionTraceSteps } = useFlowExecutionTrace({
		selectedInstanceId: displayedInstanceId,
		flowInstanceEvents,
		edges,
		nodes: structuralNodes,
		activityByName,
		selectedInstanceStatus: selectedInstanceId ? selectedInstanceRuntimeState.status : trackedRun.status,
		selectedInstanceCurrentActivityId: selectedInstanceId ? selectedInstanceRuntimeState.currentActivityId : trackedRun.currentActivityId,
	});

	useEffect(() => {
		if (trackLiveRuns && !selectedInstanceId) {
			setHistoricalExecutionSteps(trackedRun.steps);
		}
	}, [trackLiveRuns, selectedInstanceId, trackedRun.steps, setHistoricalExecutionSteps]);

	const clearSelectedInstanceRuntime = useCallback(() => {
		clearExecutionTraceSteps();
		setSelectedInstanceRuntimeState((current) => {
			if (current.status === undefined && current.currentActivityId === undefined) {
				return current;
			}

			return {};
		});
	}, [clearExecutionTraceSteps, setSelectedInstanceRuntimeState]);

	useEffect(() => {
		const status = latestStatusEvent?.payload.status;
		const activity = latestActivityEvent?.payload.currentActivityId;
		if (!status && activity === undefined) {
			return;
		}
		setSelectedInstanceRuntimeState((current) => ({
			status: (status as FlowInstanceStatus | undefined) ?? current.status,
			currentActivityId: latestActivityEvent ? (activity ?? undefined) : current.currentActivityId,
		}));
	}, [latestStatusEvent, latestActivityEvent, setSelectedInstanceRuntimeState]);

	useEffect(() => {
		const flowDefinition = flow ? (JSON.parse(flow.definition) as FlowDefinitionModel) : undefined;

		if (flowDefinition) {
			const { nodes, edges } = flowModelConverterService.toDiagramModel(flowDefinition);
			const flowName = flow?.name || flowDefinition.Name || 'New Flow';
			setNodes((currentNodes) => {
				const selectedNodeIds = new Set(currentNodes.filter((node) => node.selected).map((node) => node.id));
				return nodes.map((node) => ({ ...node, selected: selectedNodeIds.has(node.id) }));
			});
			setEdges((currentEdges) => {
				const selectedEdgeIds = new Set(currentEdges.filter((edge) => edge.selected).map((edge) => edge.id));
				return edges.map((edge) => ({ ...edge, selected: selectedEdgeIds.has(edge.id) }));
			});
			setFlowModel(flowDefinition);
			setSavedFlowSnapshot(getFlowDefinitionSnapshot(flowModelConverterService.toFlowDefinitionModel(edges, nodes, id, flowName, flowDefinition)));
		}

		if (flow && flow.name) {
			setName(flow.name);
		}
	}, [flow, id, setEdges, setNodes, setFlowModel, flowModelConverterService]);

	useEffect(() => {
		if (!id && savedFlowSnapshot === undefined) {
			setSavedFlowSnapshot(currentFlowSnapshot);
		}
	}, [currentFlowSnapshot, id, savedFlowSnapshot]);

	const addActivity = useCallback(
		(activityMetadata: ActivityMetadataModel, dropPosition?: XYPosition) => {
			const nodes = liveGraph.current.nodes;
			const base: ActivityModel = {
				Type: activityMetadata.name, // the Type must be first
				Id: uuid(),
			} as ActivityModel;

			// Apply default parameters from metadata
			let activityModel = applyObjectDefaultParameters(base, activityMetadata.parameters);

			// Auto-populate service connections from existing activities if they match type or category
			if (activityMetadata.parameters) {
				for (const param of activityMetadata.parameters) {
					// Only apply if parameter is a service connection and doesn't already have a value
					if (param.type === ParameterType.ServiceConnectionRef && !activityModel[param.name]) {
						const matchingConnection = findMatchingServiceConnection(nodes, activityByName, param.matchType, param.matchCategory);

						if (matchingConnection) {
							activityModel = {
								...activityModel,
								[param.name]: matchingConnection,
							};
						}
					}
				}
			}

			const isDocumentation = activityMetadata.categories?.includes(ActivityCategories.DOCUMENTATION) === true;

			const makeNode = (model: ActivityModel, x: number, y: number): FlowNode => {
				if (activityMetadata.name === ActivityTypes.NOTE) return nodeFromNote(model, x, y);
				if (activityMetadata.name === ActivityTypes.SECTION) return nodeFromSection(model, x, y);
				if (activityMetadata.name === ActivityTypes.GROUP_TOOL) return nodeFromGroup(model, x, y);
				if (activityMetadata.isBlock) return nodeFromBlock(model, x, y);
				return nodeFromActivity(model, x, y);
			};
			const makeCenteredNode = (model: ActivityModel, centerX: number, centerY: number): FlowNode => {
				const node = makeNode(model, 0, 0);
				const nodeWidth = node.width ?? 120;
				const nodeHeight = node.height ?? 80;

				return {
					...node,
					position: {
						x: centerX - nodeWidth / 2,
						y: centerY - nodeHeight / 2,
					},
				};
			};

			// Check if a block node is currently selected — if so, create the new node as its child
			const selectedBlockNode = nodes.find((n) => n.selected && activityByName.get(n.data?.Type)?.isBlock);

			let activityNode: FlowNode;
			if (dropPosition) {
				activityNode = makeCenteredNode(activityModel, dropPosition.x, dropPosition.y);
			} else if (selectedBlockNode) {
				// Position is relative to the parent block node — place near its top-left with a small offset
				const parentWidth = selectedBlockNode.width ?? 240;
				const parentHeight = selectedBlockNode.height ?? 160;
				const x = parentWidth / 2;
				const y = parentHeight / 2;
				const selectedBlockHidesChildren = isContainerContentHidden(selectedBlockNode.data);
				activityNode = isDocumentation
					? makeCenteredNode(activityModel, x, y)
					: { ...makeCenteredNode(activityModel, x, y), parentId: selectedBlockNode.id, hidden: selectedBlockHidesChildren };
			} else {
				// Place at the center of the visible workspace
				const rect = workspaceContainerRef.current?.getBoundingClientRect();
				const center = rect ? instance?.screenToFlowPosition({ x: (rect.left + rect.right) / 2, y: (rect.top + rect.bottom) / 2 }) : undefined;
				activityNode = makeCenteredNode(activityModel, center?.x ?? 0, center?.y ?? 0);
			}

			setNodes((prev) => [...prev, activityNode]);
		},
		[activityByName, instance, setNodes],
	);

	const handleActivityDrop = useCallback(
		(activityType: string, position: XYPosition) => {
			const activityMetadata = activityByName.get(activityType);
			if (activityMetadata) {
				addActivity(activityMetadata, position);
			}
		},
		[activityByName, addActivity],
	);

	const updateNodeData = useCallback(
		(nodeId: string, data: Partial<FlowNodeModel>) => {
			setNodes((prev) => {
				const currentNode = prev.find((node) => node.id === nodeId);
				if (currentNode && data.Metadata && (currentNode.data.Metadata?.compact === true) !== (data.Metadata.compact === true)) {
					return updateAgentServiceCompact(prev, nodeId, data.Metadata.compact === true, data);
				}
				return prev.map((n) => {
					if (n.id === nodeId) {
						return {
							...n,
							data: {
								...n.data,
								...data,
							},
						};
					}
					return n;
				});
			});
		},
		[setNodes],
	);

	const updateEdgeData = useCallback(
		(edgeId: string, data: Partial<FlowEdgeModel>) => {
			setEdges((prev) =>
				prev.map((n) => {
					if (n.id === edgeId) {
						return {
							...n,
							data: {
								...n.data,
								...data,
							} as FlowEdgeModel,
						};
					}
					return n;
				}),
			);
		},
		[setEdges],
	);

	const getLiveFlowDefinition = useCallback(
		() => flowModelConverterService.toFlowDefinitionModel(liveGraph.current.edges, liveGraph.current.nodes, id, name, flowModel),
		[flowModelConverterService, id, name, flowModel],
	);

	const saveFlowDefinition = useCallback(async () => {
		if (isSystemFlow || savePending || !hasFlowChanges) {
			return;
		}

		let definition: FlowDefinitionModel | undefined;
		try {
			setSaveError(undefined);
			definition = getLiveFlowDefinition();
			const newId = await saveFlow(setSavePending, id, name, definition, client);
			setActivityValidation(undefined);
			setSavedFlowSnapshot(getFlowDefinitionSnapshot(definition));
			toastService.success('Flow saved');
			if (newId !== id) {
				navigate(`/flow/${newId}`, { replace: true });
			}
		} catch (error) {
			const parsedError = parseFlowSaveError(error);
			setSaveError(parsedError);
			setActivityValidation({
				flowId: id,
				error: parsedError,
				issues: definition ? groupActivityValidationIssues(definition, parsedError.validationIssues) : emptyActivityValidationIssues,
			});
		}
	}, [client, getLiveFlowDefinition, hasFlowChanges, id, isSystemFlow, name, navigate, savePending, toastService]);

	const handleSaveKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (!event.ctrlKey && !event.metaKey) return;

			event.preventDefault();
			void saveFlowDefinition();
		},
		[saveFlowDefinition],
	);

	useKeyDown(SAVE_KEYS, handleSaveKeyDown, true);

	const copyFlowDefinition = useCallback(async () => {
		await copyToClipboard(getLiveFlowDefinition(), (definition) => serializeWithTypeFirst(definition, 2));
	}, [copyToClipboard, getLiveFlowDefinition]);

	const pasteFlowDefinition = useCallback(async () => {
		if (isSystemFlow) {
			return;
		}

		try {
			const clipboardText = await navigator.clipboard.readText();
			const pastedFlowDefinition = JSON.parse(clipboardText) as FlowDefinitionModel;

			if (!Array.isArray(pastedFlowDefinition.Content)) {
				toastService.error('Clipboard does not contain a valid flow');
				return;
			}

			const remappedFlowDefinition = clipboardService.remapFlowDefinitionIds(pastedFlowDefinition);
			const { nodes: pastedNodes, edges: pastedEdges } = flowModelConverterService.toDiagramModel(remappedFlowDefinition);
			setNodes(pastedNodes);
			setEdges(pastedEdges);
			setFlowModel(remappedFlowDefinition);
			setName(remappedFlowDefinition.Name || 'New Flow');
			toastService.success('Flow pasted from clipboard');
		} catch {
			toastService.error('Failed to paste flow from clipboard');
		}
	}, [clipboardService, flowModelConverterService, isSystemFlow, setEdges, setNodes, toastService]);

	const handleOpenActionsMenu = (event: React.MouseEvent<HTMLElement>) => {
		setActionsMenuAnchor(event.currentTarget);
	};

	const handleCloseActionsMenu = () => {
		setActionsMenuAnchor(null);
	};

	const handleCopyFlowFromMenu = async () => {
		handleCloseActionsMenu();
		await copyFlowDefinition();
	};

	const handlePasteFlowFromMenu = async () => {
		handleCloseActionsMenu();
		await pasteFlowDefinition();
	};

	const playFlow = () => {
		if (!id) {
			toastService.error('Please save the flow before running it');
			return;
		}
		setTrackLiveRuns(true);
		setRunFlowModalOpen(true);
	};

	const handleViewFlowInstance = (instance: FlowInstanceViewModel) => {
		// Update URL query params
		selectInstance(instance.id);
	};

	const handleCloseInstancesPanel = () => {
		setInstancesPanelOpen(false);
		setInstancesPanelCollapsed(false);
		// Also close the details panel when instances panel is closed
		clearSelectedInstanceRuntime();
		// Remove instanceId from URL
		clearInstance();
	};

	const selectNode = useCallback(
		(activityId: string | null) => {
			setNodes((prev) =>
				prev.map((n) => ({
					...n,
					selected: n.id === activityId,
				})),
			);
		},
		[setNodes],
	);

	useEffect(() => {
		if (selectedInstanceId) {
			setInstancesPanelOpen(true);
			setInstancesPanelCollapsed(false);
		} else {
			selectNode(null);
		}
	}, [clearSelectedInstanceRuntime, selectNode, selectedInstanceId]);

	const handleCloseInstanceDetails = () => {
		clearSelectedInstanceRuntime();
		// Deselect all nodes when closing the panel
		selectNode(null);
		// Remove instanceId from URL
		clearInstance();
	};

	const handleInstanceDelete = (instanceId: string) => {
		// Clear selection if the deleted instance was selected
		if (selectedInstanceId === instanceId) {
			clearSelectedInstanceRuntime();
			// Remove instanceId from URL
			clearInstance();
		}
		// Trigger refresh of instances list
		setInstancesRefreshTrigger((prev) => prev + 1);
	};

	const handleFlowRunSuccess = (instanceId: string) => {
		// Show the new run in the list and open its details as soon as creation returns its ID.
		setInstancesRefreshTrigger((current) => current + 1);
		setInstancesPanelOpen(true);
		setInstancesPanelCollapsed(false);
		selectInstance(instanceId);
	};

	const handleInstanceResumed = useCallback(() => {
		setInstancesRefreshTrigger((prev) => prev + 1);
	}, []);

	const handleInstanceStatusChanged = useCallback(
		(status: FlowInstanceStatus | undefined) => {
			setSelectedInstanceRuntimeState((current) => {
				if (current.status === status) {
					return current;
				}

				return {
					...current,
					status,
				};
			});
		},
		[setSelectedInstanceRuntimeState],
	);

	const handleInstanceCurrentActivityChanged = useCallback(
		(currentActivityId: string | undefined) => {
			setSelectedInstanceRuntimeState((current) => {
				if (current.currentActivityId === currentActivityId) {
					return current;
				}

				return {
					...current,
					currentActivityId,
				};
			});
		},
		[setSelectedInstanceRuntimeState],
	);

	const entitySchemaRepository = useMemo<IEntitySchemaRepository>(() => new EntitySchemaRepository(client), [client]);
	const closeDrawer = useCallback(() => setActionPanelOpen(false), []);
	const collapseActionPanel = useCallback(() => setActionPanelCollapsed(true), []);
	const actionPanel = (drawer = false) => (
		<MemoizedActionPanel
			flowDefinitionId={id}
			flow={flowModel}
			entitySchemaRepository={entitySchemaRepository}
			addActivity={addActivity}
			onFlowChanged={setFlowModel}
			onNodeDataChanged={updateNodeData}
			onEdgeDataChanged={updateEdgeData}
			readOnly={isSystemFlow}
			drawer={drawer}
			onClose={drawer ? closeDrawer : collapseActionPanel}
		/>
	);
	const schemaUsageCounts = useMemo(
		() => countFlowSchemaUses(flowModel, nodes as { data: Record<string, unknown> }[]),
		[flowModel.InputSchemaValue, flowModel.OutputSchemaValue, nodes],
	);

	if (flowLoading) {
		return <ViewLoader />;
	}

	return (
		<PresenceContext.Provider value={flowViewers}>
			<FlowSchemaContext.Provider value={{ flow: flowModel, onFlowChanged: setFlowModel, usageCounts: schemaUsageCounts }}>
				<ExpressionMetadataProvider expressions={flowMetadata.expressions} expressionScopeCalculator={flowExpressionScopeCalculator}>
					<FlowValidationContext.Provider value={visibleValidationIssues}>
						<ReactFlowProvider key={id ?? 'new'}>
							<Box sx={(theme) => ({ px: `${theme.layout.edgeGap}px`, pt: `${theme.layout.edgeGap}px` })}>
								<ViewTitleBar
									title={name}
									onTitleChanged={isSystemFlow ? undefined : setName}
									titleAdornment={
										<>
											<PresenceIndicator viewers={flowViewers} label="Also in this Flow" showLabel />
											<PresenceIndicator viewers={selectionViewers} label="Viewing the same selection" showLabel />
										</>
									}
								>
									<Stack direction="row" spacing={1} alignItems="center">
										{isSystemFlow && <Chip label="System" color="info" variant="outlined" size="small" />}
										{isSystemFlow && connectionBindings && !connectionBindings.isReady && <Chip label="Setup required" color="warning" size="small" />}
										{isSystemFlow && connectionBindingsError && <Chip label="Connection setup unavailable" color="error" size="small" />}
										{validationIssueCount > 0 && (
											<Button
												variant="outlined"
												color="error"
												onClick={() => setSaveError(activityValidation?.error)}
												aria-label={`Show ${validationIssueCount} validation issues`}
												title={`${validationIssueCount} validation issues`}
												sx={{ width: 48, minWidth: 48, px: 0, flexShrink: 0 }}
											>
												{validationIssueCount}
											</Button>
										)}
										{isSystemOwned && (
											<Button variant="outlined" onClick={() => setBindingsDialogOpen(true)}>
												{isSystemFlow ? 'Configure connections' : 'Connection policies'}
											</Button>
										)}
										{isMobile && (
											<Button aria-label="Open flow tools" title="Open flow tools" onClick={() => setActionPanelOpen(true)} sx={{ minWidth: 48 }}>
												<TuneIcon />
											</Button>
										)}
										<Typography variant="body2" color="text.secondary" sx={{ '&&': { ml: 2 } }}>
											Runs
										</Typography>
										<ButtonGroup variant="outlined" aria-label="flow run actions" sx={{ '& .MuiButton-root': { width: 40, minWidth: 40, px: 0 } }}>
											<Button
												variant="contained"
												aria-label="Run flow"
												title="Run flow"
												disabled={!id || (isSystemFlow && connectionBindings?.isReady !== true)}
												onClick={playFlow}
											>
												<PlayIcon />
											</Button>
											<Button
												variant={trackLiveRuns ? 'contained' : 'outlined'}
												aria-label="Track live runs"
												aria-pressed={trackLiveRuns}
												title={trackLiveRuns ? 'Stop tracking live runs' : 'Track live runs'}
												disabled={!id}
												onClick={() => setTrackLiveRuns((value) => !value)}
											>
												<SensorsIcon />
											</Button>
											<Button
												variant="outlined"
												aria-label="View flow runs"
												title="View flow runs"
												disabled={!id}
												onClick={() => {
													setInstancesPanelOpen(true);
													setInstancesPanelCollapsed(false);
												}}
											>
												<HistoryIcon />
											</Button>
										</ButtonGroup>
										<ButtonGroup variant="outlined" aria-label="flow edit actions" sx={{ '&&': { ml: 2 } }}>
											<Button
												variant="contained"
												startIcon={<SendIcon />}
												disabled={isSystemFlow || savePending || !hasFlowChanges}
												onClick={saveFlowDefinition}
											>
												Save
											</Button>
											<Button variant="outlined" aria-label="More flow actions" title="More actions" onClick={handleOpenActionsMenu}>
												<MoreVertIcon />
											</Button>
										</ButtonGroup>
									</Stack>
									<Menu anchorEl={actionsMenuAnchor} open={Boolean(actionsMenuAnchor)} onClose={handleCloseActionsMenu}>
										<MenuItem onClick={handleCopyFlowFromMenu}>
											<ListItemIcon>
												<ContentCopyIcon fontSize="small" />
											</ListItemIcon>
											<ListItemText>Copy flow</ListItemText>
										</MenuItem>
										{!isSystemFlow && (
											<MenuItem onClick={handlePasteFlowFromMenu}>
												<ListItemIcon>
													<ContentPasteIcon fontSize="small" />
												</ListItemIcon>
												<ListItemText>Paste flow</ListItemText>
											</MenuItem>
										)}
									</Menu>
								</ViewTitleBar>
							</Box>
							<Stack
								direction="row"
								sx={{
									flexGrow: 1,
									flexShrink: 1,
									minHeight: 0,
									overflow: 'hidden',
									gap: (theme) => `${theme.layout.panelGap}px`,
									px: (theme) => `${theme.layout.edgeGap}px`,
									py: (theme) => `${theme.layout.edgeGap}px`,
								}}
							>
								<ErrorBoundary title="Flow canvas unavailable" resetKeys={[id]} fullHeight>
									<Paper sx={{ flexGrow: 1, minWidth: 0, minHeight: 0, overflow: 'hidden' }} ref={workspaceContainerRef}>
										<FlowWorkspace
											nodes={nodes}
											setNodes={setNodes}
											edges={edges}
											setEdges={setEdges}
											executionTrace={executionTrace}
											setInstance={setInstance}
											onActivityDrop={handleActivityDrop}
											readOnly={isSystemFlow}
										/>
										{!isSystemFlow && (
											<PasteHandler
												instance={instance}
												setNodes={setNodes}
												setEdges={setEdges}
												toastService={toastService}
												workspaceContainerRef={workspaceContainerRef}
											/>
										)}
									</Paper>
								</ErrorBoundary>
								{!isMobile && !actionPanelCollapsed && actionPanel()}
								{!isMobile && actionPanelCollapsed && (
									<Paper sx={{ width: 48, flexShrink: 0, pt: 1, textAlign: 'center' }}>
										<Tooltip title="Expand tools panel">
											<IconButton aria-label="Expand tools panel" onClick={() => setActionPanelCollapsed(false)}>
												<TuneIcon />
											</IconButton>
										</Tooltip>
									</Paper>
								)}
							</Stack>
							<Drawer
								anchor="right"
								open={isMobile && actionPanelOpen}
								onClose={() => setActionPanelOpen(false)}
								PaperProps={{ sx: { width: 'min(100vw, 420px)', maxWidth: '100%' } }}
							>
								{isMobile && actionPanelOpen && actionPanel(true)}
							</Drawer>
						</ReactFlowProvider>
					</FlowValidationContext.Provider>

					<RunFlowModal
						open={runFlowModalOpen}
						onClose={() => setRunFlowModalOpen(false)}
						flowId={id!}
						flowName={name}
						onSuccess={handleFlowRunSuccess}
						inputSchemaValue={resolveFlowSchemaValue(flowModel, flowModel.InputSchemaValue)}
					/>

					<FlowSaveErrorDialog error={saveError} onClose={() => setSaveError(undefined)} />
					{isSystemOwned && id && <FlowConnectionBindingsDialog flowId={id} open={bindingsDialogOpen} onClose={() => setBindingsDialogOpen(false)} />}

					<ResponsivePanelContainer
						isMobile={isMobile}
						open={instancesPanelOpen || !!selectedInstanceId}
						onClose={handleCloseInstancesPanel}
						ariaLabel="Flow runs"
					>
						{instancesPanelOpen && id && (
							<FlowInstancesPanel
								definitionId={id}
								selectedInstanceId={selectedInstanceId}
								isCollapsed={instancesPanelCollapsed}
								onClose={handleCloseInstancesPanel}
								onViewRuns={handleViewFlowInstance}
								onCollapseChange={setInstancesPanelCollapsed}
								top={FLOW_INSTANCE_PANELS_TOP}
								width={instancesPanelWidth}
								setWidth={setInstancesPanelWidth}
								refreshTrigger={instancesRefreshTrigger}
							/>
						)}

						{selectedInstanceId && (
							<ErrorBoundary title="Flow instance details unavailable" resetKeys={[selectedInstanceId]}>
								<FlowInstanceDetailsPanel
									key={selectedInstanceId}
									instanceId={selectedInstanceId}
									nodes={nodes}
									onClose={handleCloseInstanceDetails}
									onSelectNode={selectNode}
									onInstanceChanged={handleInstanceDelete}
									onInstanceResumed={handleInstanceResumed}
									onInstanceCanceled={handleInstanceResumed}
									onExecutionStepsChanged={setHistoricalExecutionSteps}
									onInstanceStatusChanged={handleInstanceStatusChanged}
									onInstanceCurrentActivityChanged={handleInstanceCurrentActivityChanged}
									listPanelCollapsed={instancesPanelCollapsed}
									top={FLOW_INSTANCE_PANELS_TOP}
									rightOffset={instancesPanelWidth}
								/>
							</ErrorBoundary>
						)}
					</ResponsivePanelContainer>
				</ExpressionMetadataProvider>
			</FlowSchemaContext.Provider>
		</PresenceContext.Provider>
	);
}

export default function FlowDesignerView() {
	return (
		<Stack direction="column" sx={{ flexGrow: 1, minHeight: 0 }}>
			<ServiceConnectionMetadataProvider>
				<FlowMetadataProvider>
					<FlowDesignerContent />
				</FlowMetadataProvider>
			</ServiceConnectionMetadataProvider>
		</Stack>
	);
}
